import React, { useState, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Asset } from '@/types/models/assets.types';
import { BaseDrawer, BaseTooltip } from '@/components';
import { PenIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useIsDMAdmin } from '../../shared';
import { MaintenanceDataForm } from '@/modules/shared/Maintenance/MaintenanceForm/MaintenanceDataForm';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';

export const useMaintenanceTable = ({
  DMComponents,
  data = [],
  hideEmptySecondaryWorkOrder = true,
}: {
  DMComponents: any;
  data?: any[];
  hideEmptySecondaryWorkOrder?: boolean;
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const { isDMAdmin } = useIsDMAdmin();
  const closeDrawer = () => {
    setIsEditDrawerOpen(false);
  };

  const renderEditRow = (row: any) => {
    if (!isDMAdmin) return null;
    return (
      <div className="items-center flex h-full justify-center opacity-0 group-hover:opacity-100 px-4 max-h-screen">
        <BaseDrawer
          isOpen={isEditDrawerOpen} // Controlled by parent
          onClose={() => setIsEditDrawerOpen(false)} // Close handler passed from parent
          title="Edit Data"
          content={
            <div className="min-w-[400px] max-w-fit h-full  flex flex-col items-center overflow-y-auto min-h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] overflow-x-hidden">
              <Separator className="h-[1px] bg-gray-400 mx-2" orientation="horizontal" />
              {/* Pass closeDrawer function as a prop */}
              <MaintenanceDataForm row={selectedRow} DMComponents={DMComponents} closeDrawer={closeDrawer} />
            </div>
          }
          direction="right"
        >
          <Button
            variant={'ghost'}
            size={'sm'}
            className="h-8 w-8"
            onClick={() => {
              setIsEditDrawerOpen(true);
              setSelectedRow(row.original);
            }}
            data-testid="edit"
          >
            <PenIcon fontSize={24} />
          </Button>
        </BaseDrawer>
      </div>
    );
  };

  const renderDateTimeCell = (info: any) => {
    const rawDate = info.getValue();
    let formattedDate = '';

    try {
      const parsedDate = parseISO(rawDate);
      formattedDate = format(parsedDate, 'dd/MM/yy HH:mm');
    } catch (e) {
      formattedDate = rawDate;
    }

    return (
      <BaseTooltip content={rawDate} classNames={{ content: 'max-w-[400px]' }}>
        <div className={cellClassName}>{formattedDate}</div>
      </BaseTooltip>
    );
  };

  const renderNestedRowCell = (nestedRow: any, column: any) => {
    if (column.accessorKey === 'severity') {
      return (
        <div className={`bg-${nestedRow.severity} w-fit h-10 flex text-white capitalize items-center rounded-xl px-6`}>
          {nestedRow.severity}
        </div>
      );
    }

    return nestedRow[column.accessorKey];
  };
  const renderTriggerRow = (row: any) => row.original.name;
  const cellClassName = 'whitespace-pre-line text-left line-clamp-4 text-sm capitalize';

  // @TODO: remove the condition/true once product decides => using tmp true val.
  // Check if any row has a non-null secondaryWorkOrderId
  const hasSecondaryWorkOrderId =
    true ||
    useMemo(
      () =>
        !hideEmptySecondaryWorkOrder ||
        data.some(
          (row) =>
            row.secondaryWorkOrderId !== null &&
            row.secondaryWorkOrderId !== undefined &&
            row.secondaryWorkOrderId !== ''
        ),
      [data, hideEmptySecondaryWorkOrder]
    );

  const columns: ColumnDef<Asset>[] = useMemo(
    () => [
      {
        accessorKey: 'workOrderId',
        header: 'Work Order ID',
        cell: (info) => info.getValue(),
        enableFiltering: true,
        enableSearch: true,
      },
      ...(hasSecondaryWorkOrderId
        ? [
            {
              accessorKey: 'secondaryWorkOrderId',
              header: 'Secondary Work Order ID',
              cell: (info: any) => info.getValue(),
              enableFiltering: true,
              enableSearch: true,
            },
          ]
        : []),
      {
        accessorKey: 'orderType',
        header: 'Order Type',
        cell: (info) => info.getValue(),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'description',
        header: 'Actual Work Description',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cn(cellClassName, 'w-[350px]', 'text-xs')}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'equipmentNumber',
        header: 'Equipment Number',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'clientComponentName',
        header: 'Component Name',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'componentUiName',
        header: 'Razor Component Name',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'maintenanceActivityType',
        header: 'Maintenance Activity Type',
        cell: (info: any) => (
          <div className="text-sm whitespace-pre-line text-left max-w-[150px] min-w-[150px] line-clamp-2  ">
            {info.getValue()}
          </div>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'actualStartDate',
        header: 'Actual Start Date',
        cell: renderDateTimeCell,
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'actualEndDate',
        header: 'Actual End Date',
        cell: renderDateTimeCell,
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'workOrderStatus',
        header: 'Work Order Status',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'laborHours',
        header: 'Labor Hours',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      // {
      //   accessorKey: 'dataSource',
      //   header: 'Data Source',
      //   cell: (info: any) => (
      //     <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
      //       <div className={cellClassName}>{info.getValue()}</div>
      //     </BaseTooltip>
      //   ),
      //   enableFiltering: true,
      // },

      {
        accessorKey: 'personResponsible',
        header: 'Person Responsible',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'priority',
        header: 'Priority',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'plantNumber',
        header: 'Plant Number',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px]' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'razorComment',
        header: 'Razor Comments',
        cell: (info: any) => (
          <BaseTooltip content={info.getValue()} classNames={{ content: 'max-w-[400px] ' }}>
            <div className={cellClassName}>{info.getValue()}</div>
          </BaseTooltip>
        ),
        enableFiltering: true,
        enableSearch: true,
      },
    ],
    [expandedRows, hasSecondaryWorkOrderId]
  );

  return {
    tableStructure: columns,
    expandedRows,
    setExpandedRows,
    renderNestedRowCell,
    renderTriggerRow,
    renderEditRow,
  };
};
